import { useCallback, useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { HeaderMenuDropdownProps } from "../interfaces";

// Icons
import { ReactComponent as InformationIcon } from "@/assets/icons/header/information.svg";
// Helpers
import calculateSubmenuHeight from "../helpers/calculateSubmenuHeight";

// Custom components
import HeaderMenuFooter from "./HeaderMenuFooter";
import SubMenu from "./SubMenu";

// MUI
import { Box, Button, Stack } from "@mui/material";

export default function HeaderMenuDropdown(props: Readonly<HeaderMenuDropdownProps>) {
	const { sections, footerItems, submenuMinWidth, submenuHeight } = props;

	const trackedDiv = useRef<HTMLDivElement>(null);

	let minFixedHeight: number | null = null;

	if (typeof submenuHeight === "number") {
		minFixedHeight = submenuHeight;
	}

	if (submenuHeight === "calculated") {
		minFixedHeight = calculateSubmenuHeight(props);
	}

	const [wrapperHeight, setWrapperHeight] = useState(minFixedHeight);

	const handleMenuClick = (menuName: string) => {
		setSelectedSection(menuName);
	};

	const checkAccess = useCallback(
		(index: number) => {
			for (const sectionGroup of sections[index].menuItemGroups) {
				for (const menuItem of sectionGroup.menuItems) {
					if (menuItem.hasAccess) {
						return true;
					}
				}
			}
			return false;
		},
		[sections]
	);

	const findFirstDepartmentWithAccess = useCallback(() => {
		for (const [index, menuSection] of sections.entries()) {
			if (checkAccess(index)) {
				return menuSection.name;
			}
		}
		return "";
	}, [sections, checkAccess]);

	const [selectedSection, setSelectedSection] = useState(findFirstDepartmentWithAccess);

	useEffect(() => {
		if (selectedSection === "") {
			setSelectedSection(findFirstDepartmentWithAccess);
		}
	}, [selectedSection, findFirstDepartmentWithAccess]);

	const [showDescription, setShowDescription] = useState(false);

	useEffect(() => {
		const currentSection = sections.find((section) => section.name === selectedSection) ?? { name: "", menuItemGroups: [] };
		if (!currentSection) {
			return;
		}
		let newSize = calculateSubmenuHeight({ sections: [currentSection] }, showDescription);
		if (minFixedHeight && newSize < minFixedHeight) {
			newSize = minFixedHeight;
		}
		setWrapperHeight(newSize);
	}, [minFixedHeight, sections, selectedSection, showDescription]);

	return (
		<Stack className="flex-nowrap relative" style={{ minWidth: `${submenuMinWidth}px` }}>
			<Stack
				className="overflow-hidden h-full"
				style={{
					maxHeight: wrapperHeight ? `${wrapperHeight}px` : "auto",
					minHeight: wrapperHeight ? `${wrapperHeight}px` : "auto",
					transition: "max-height 400ms ease, min-height 400ms ease",
				}}
			>
				<div ref={trackedDiv}>
					<Stack direction={"row"} className="p-8">
						<Stack className="flex-initial mr-6">
							{sections.map((menuItem, index) => {
								const isDisabled = !checkAccess(index);
								return (
									<Button
										key={menuItem.name}
										className={clsx("border-button-motivators-menu", {
											"header-menu-button": true,
											selected: selectedSection === menuItem.name,
											"disabled-header-menu-button": isDisabled,
										})}
										variant="contained"
										disableElevation
										disabled={isDisabled}
										onMouseEnter={() => handleMenuClick(menuItem.name)}
										onClick={() => handleMenuClick(menuItem.name)}
									>
										<Stack className="w-full text-left text-nowrap">{menuItem.name}</Stack>
									</Button>
								);
							})}
						</Stack>
						<Stack className="flex grow menu-header-content -mt-2">
							<SubMenu
								showDescription={showDescription}
								menuSection={
									sections.find((section) => section.name === selectedSection) ?? { name: "", menuItemGroups: [] }
								}
							/>
						</Stack>
					</Stack>
				</div>
			</Stack>
			<HeaderMenuFooter footerItems={footerItems} />
			<Box className="absolute top-2 right-2 m-1">
				<InformationIcon
					className="text-default"
					onMouseEnter={() => setShowDescription(true)}
					onMouseLeave={() => setShowDescription(false)}
				/>
			</Box>
		</Stack>
	);
}
